import { SupportedLanguage } from "../../language/supportedLanguages";

export interface SiteLink {
  href: string;
  label: string;
}

export interface SiteNavigationItem extends SiteLink {
  children?: SiteLink[];
  isCallToAction?: boolean;
}

export interface SiteLinksConfig {
  navigationItems: SiteNavigationItem[];
  footerLinks: SiteLink[];
  languageOrder: SupportedLanguage[];
}

export const siteLinks: Record<SupportedLanguage, SiteLinksConfig> = {
  de: {
    navigationItems: [
      {
        href: "https://esg2go.org/angebot-preise/",
        label: "Angebot & Preise",
        children: [
          { href: "https://esg2go.org/reportrunner/", label: "ReportRunner" },
          { href: "https://esg2go.org/faq/", label: "FAQ" },
        ],
      },
      {
        href: "https://esg2go.org/pros-partner/",
        label: "Partner & Community",
        children: [
          { href: "https://esg2go.org/community/", label: "Community" },
          {
            href: "https://esg2go.org/auditierte-unternehmen/",
            label: "Auditierte Unternehmen",
          },
        ],
      },
      {
        href: "https://esg2go.org/ueber-uns/",
        label: "Über uns",
        children: [
          { href: "https://esg2go.org/blog/", label: "Blog" },
          {
            href: "https://esg2go.org/medienspiegel/",
            label: "Medienspiegel",
          },
          {
            href: "https://esg2go.org/team-geschichte/",
            label: "Team und Geschichte",
          },
          {
            href: "https://esg2go.org/datensicherheit/",
            label: "Datensicherheit",
          },
          {
            href: "https://esg2go.org/transparenz/",
            label: "Transparenz",
          },
        ],
      },
      {
        href: "https://esg2go.net",
        label: "Daten eingeben",
        isCallToAction: true,
      },
    ],
    footerLinks: [
      { href: "https://esg2go.org/impressum/", label: "Impressum" },
      {
        href: "https://esg2go.org/datenschutzbestimmungen/",
        label: "Datenschutzbestimmungen",
      },
    ],
    languageOrder: ["en", "it", "fr", "es"],
  },
  en: {
    navigationItems: [
      {
        href: "https://esg2go.org/en/offer-and-prices/",
        label: "Offer & Prices",
        children: [
          { href: "https://esg2go.org/en/reportrunner/", label: "ReportRunner" },
          { href: "https://esg2go.org/en/faq/", label: "FAQ" },
        ],
      },
      {
        href: "https://esg2go.org/en/pros-partners/",
        label: "Partner & Community",
        children: [
          { href: "https://esg2go.org/en/community/", label: "Community" },
          {
            href: "https://esg2go.org/en/certification-without-effort/",
            label: "Audited Companies",
          },
        ],
      },
      {
        href: "https://esg2go.org/en/about-us/",
        label: "About us",
        children: [
          {
            href: "https://esg2go.org/en/news-from-esg2go/",
            label: "Blog",
          },
          {
            href: "https://esg2go.org/en/media-review/",
            label: "Media review",
          },
          {
            href: "https://esg2go.org/en/team-and-advisory-board/",
            label: "Team and history",
          },
          {
            href: "https://esg2go.org/en/data-security/",
            label: "Data Security",
          },
          {
            href: "https://esg2go.org/en/foundation/",
            label: "Transparency",
          },
        ],
      },
      {
        href: "https://www.esg2go.net/esg2go/default.aspx?sprachcode=en",
        label: "Data Input",
        isCallToAction: true,
      },
    ],
    footerLinks: [
      { href: "https://esg2go.org/en/imprint/", label: "Imprint" },
      {
        href: "https://esg2go.org/en/data-security/",
        label: "Data Security",
      },
    ],
    languageOrder: ["it", "fr", "de", "es"],
  },
  fr: {
    navigationItems: [
      {
        href: "https://esg2go.org/fr/offre-prix/",
        label: "Offre & prix",
        children: [
          {
            href: "https://esg2go.org/fr/certification-sans-effort/",
            label: "ReportRunner",
          },
          { href: "https://esg2go.org/fr/faq/", label: "FAQ" },
        ],
      },
      {
        href: "https://esg2go.org/fr/pros-partenaires/",
        label: "Partenaires & Communauté",
        children: [
          { href: "https://esg2go.org/fr/community/", label: "Communauté" },
          {
            href: "https://esg2go.org/fr/entreprises-auditees/",
            label: "Entreprises auditées",
          },
        ],
      },
      {
        href: "https://esg2go.org/fr/a-propos-de-nous/",
        label: "À propos de nous",
        children: [
          {
            href: "https://esg2go.org/fr/actualites-desg2go/",
            label: "Actualités d’esg2go",
          },
          {
            href: "https://esg2go.org/fr/revue-de-presse/",
            label: "Revue de presse",
          },
          {
            href: "https://esg2go.org/fr/equipe-et-conseil-consultatif/",
            label: "Équipe et histoire",
          },
          {
            href: "https://esg2go.org/fr/securite-des-donnees/",
            label: "Sécurité des données",
          },
          {
            href: "https://esg2go.org/fr/fondation/",
            label: "Transparence",
          },
        ],
      },
      {
        href: "https://www.esg2go.net/esg2go/default.aspx?sprachcode=fr",
        label: "Saisie des données",
        isCallToAction: true,
      },
    ],
    footerLinks: [
      {
        href: "https://esg2go.org/fr/impressum/",
        label: "Mentions légales",
      },
      {
        href: "https://esg2go.org/fr/politique-de-confidentialite/",
        label: "Politique de confidentialité",
      },
    ],
    languageOrder: ["en", "it", "de", "es"],
  },
  it: {
    navigationItems: [
      {
        href: "https://esg2go.org/it/offerte-e-prezzi/",
        label: "Offerte e prezzi",
        children: [
          { href: "https://esg2go.org/it/reportrunner/", label: "ReportRunner" },
          { href: "https://esg2go.org/it/faq/", label: "FAQ" },
        ],
      },
      {
        href: "https://esg2go.org/it/professionisti-e-partner/",
        label: "Partner e Comunità",
        children: [
          { href: "https://esg2go.org/it/comunita/", label: "Comunità" },
          {
            href: "https://esg2go.org/it/societa-sottoposte-a-revisione/",
            label: "Società sottoposte a revisione",
          },
        ],
      },
      {
        href: "https://esg2go.org/it/chi-siamo/",
        label: "Su di noi",
        children: [
          { href: "https://esg2go.org/it/blog/", label: "Blog" },
          {
            href: "https://esg2go.org/it/recensione-dei-media/",
            label: "Recensione dei media",
          },
          {
            href: "https://esg2go.org/it/squadra-e-storia/",
            label: "Squadra e storia",
          },
          {
            href: "https://esg2go.org/it/sicurezza-dei-dati/",
            label: "Sicurezza dei dati",
          },
          {
            href: "https://esg2go.org/it/trasparenza/",
            label: "Transparenza",
          },
        ],
      },
      {
        href: "https://www.esg2go.net/esg2go/default.aspx?sprachcode=it",
        label: "INSERIRE DATI",
        isCallToAction: true,
      },
    ],
    footerLinks: [
      { href: "https://esg2go.org/it/impronta/", label: "Impronta" },
      {
        href: "https://esg2go.org/it/normativa-sulla-protezione-dei-dati/",
        label: "Normativa sulla protezione dei dati",
      },
    ],
    languageOrder: ["en", "fr", "de", "es"],
  },
  es: {
    navigationItems: [
      {
        href: "https://esg2go.org/es/oferta-y-precios/",
        label: "Ofertas y precios",
        children: [
          { href: "https://esg2go.org/es/reportrunner/", label: "ReportRunner" },
          { href: "https://esg2go.org/es/preguntas-frecuentes/", label: "PREGUNTAS FRECUENTES" },
        ],
      },
      {
        href: "https://esg2go.org/es/profesionales-y-socios/",
        label: "Socios y Comunidad",
        children: [
          { href: "https://esg2go.org/es/comunidad/", label: "Comunidad" },
          {
            href: "https://esg2go.org/es/empresas-auditadas/",
            label: "Empresas auditadas",
          },
        ],
      },
      {
        href: "https://esg2go.org/es/sobre-nosotros/",
        label: "Sobre nosotros",
        children: [
          { href: "https://esg2go.org/es/blog/", label: "Blog" },
          {
            href: "https://esg2go.org/es/revision-de-los-medios-de-comunicacion/",
            label: "Reseñas de los medios de comunicación",
          },
          {
            href: "https://esg2go.org/es/equipo-e-historia/",
            label: "Equipo e historia",
          },
          {
            href: "https://esg2go.org/es/seguridad-de-los-datos/",
            label: "Protección de datos",
          },
          {
            href: "https://esg2go.org/es/transparencia/",
            label: "Transparencia",
          },
        ],
      },
      {
        href: "https://www.esg2go.net/esg2go/default.aspx?sprachcode=es",
        label: "INTRODUCIR DATOS",
        isCallToAction: true,
      },
    ],
    footerLinks: [
      { href: "https://esg2go.org/es/pie-de-imprenta/", label: "Aviso legal" },
      {
        href: "https://esg2go.org/es/normativa-sobre-proteccion-de-datos/",
        label: "Política de privacidad",
      },
    ],
    languageOrder: ["en", "it", "fr", "de"],
  },
};